import { sqSeriesRuntimeStore, sqTrendSeriesStore } from '@/core/core.stores';
import { getChartWidth } from '@/trendData/chartWidth.utilities';
import { getDurationDisplayRange } from '@/trendData/duration.utilities';
import { affectsSeriesRenderData } from '@/trendData/seriesData.utilities';
import { CHILD_CLONED_PROPERTIES, ITEM_CHILDREN_TYPES, SAMPLE_OPTIONS } from '@/trendData/trendData.constants';
import type { CustomizationPayload } from '@/trendData/itemStoreBase.types';
import type { SeriesDataContext } from '@/trendData/seriesData.utilities';
import type { SeriesDataConfig } from '@/trendData/seriesRuntime.store';
import type { ShadedAreaBound } from '@/trendData/trendSeries.store';

/**
 * The chart geometry and visible range the sample transforms depend on. Gathered here rather than in
 * the runtime store, which must not read other stores.
 */
export function getSeriesDataContext(): SeriesDataContext {
  const { start, end, duration } = getDurationDisplayRange();
  return {
    chartWidth: getChartWidth(),
    displayRange: { start: start.valueOf(), end: end.valueOf(), duration: duration.asMilliseconds() },
  };
}

/** The item configuration the sample transforms read, gathered from the item that owns the data. */
export function getSeriesDataConfig(item: {
  color?: string;
  lineWidth?: number;
  sampleDisplayOption?: string;
  shadedAreaLower?: ShadedAreaBound | null;
  shadedAreaUpper?: ShadedAreaBound | null;
  childType?: string;
  startTime?: number;
}): SeriesDataConfig {
  return {
    color: item.color,
    lineWidth: item.lineWidth,
    minTime: item.childType === ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE ? item.startTime : undefined,
    hasBounds: item.shadedAreaLower != null && item.shadedAreaUpper != null,
    sampleDisplayOption: item.sampleDisplayOption ?? SAMPLE_OPTIONS.LINE,
  };
}

/**
 * Re-derives the render data of every series a customization change affects, since the display option
 * and line width feed the sample transforms. Children are included because they inherit both.
 *
 * TODO CRAB-57427: same flux/runtime straddle as recolorSeriesZones — goes away when fetch-derived
 * children move into the runtime stores.
 */
export function rebuildSeriesRenderData(payload: { items: CustomizationPayload[] }): void {
  const affected = payload.items.filter((itemPayload) => affectsSeriesRenderData(itemPayload));

  if (affected.length === 0) {
    return;
  }

  const context = getSeriesDataContext();
  affected.forEach((itemPayload) => {
    const item = sqTrendSeriesStore.findItem(itemPayload.id);

    // A child rebuilds only when it inherits a changed field; an unrelated parent change must not
    // re-derive its data under a newer chart context
    const children = sqTrendSeriesStore.findChildren(itemPayload.id).filter((child) => {
      const cloned = (CHILD_CLONED_PROPERTIES[child.childType as keyof typeof CHILD_CLONED_PROPERTIES] ??
        []) as readonly string[];
      const inherited = Object.fromEntries(Object.entries(itemPayload).filter(([key]) => cloned.includes(key)));
      return affectsSeriesRenderData(inherited);
    });

    [...(item ? [item] : []), ...children].forEach((seriesItem) =>
      sqSeriesRuntimeStore.actions.rebuildData(seriesItem.id, {
        config: getSeriesDataConfig(seriesItem),
        context,
      }),
    );
  });
}

/**
 * Recolors the uncertainty zones of every series a color change affects. Series from a capsule take
 * their color from the signal they came from.
 *
 * TODO CRAB-57427: fetch-derived children (series from capsules, metric display series) belong in the
 * runtime stores; once they move, the fan-out below reads and writes one store instead of straddling
 * the flux item store and the runtime store.
 */
export function recolorSeriesZones(id: string, color: string | undefined): void {
  const item = sqTrendSeriesStore.findItem(id);
  const affected = item ? [item] : [];

  sqTrendSeriesStore.findChildren(id).forEach((child) => {
    const inheritsColor = (
      CHILD_CLONED_PROPERTIES[child.childType as keyof typeof CHILD_CLONED_PROPERTIES] as readonly string[] | undefined
    )?.includes('color');
    const fromAnotherSignal = child.childType === ITEM_CHILDREN_TYPES.SERIES_FROM_CAPSULE && child.interestId !== id;

    if (inheritsColor && !fromAnotherSignal) {
      affected.push(child);
    }
  });

  affected.forEach((seriesItem) => sqSeriesRuntimeStore.actions.setZoneColor(seriesItem.id, color));
}
